import { HttpClient, HttpContext } from '@angular/common/http';
import { SUPPRESS_ERROR_TOAST } from '../common/http-context-tokens';
import { Injectable } from '@angular/core';
import {
  Observable,
  catchError,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  AddressOption,
  BankData,
  CountryData,
  IdentityDocumentTypeData,
  IdTypeOption,
  LocalGovernmentData,
  StateData,
} from '../models/country-address';
import { DataService } from './data.service';

/** The geography fields core stores on an address (see withFullGeo). */
export interface SavedGeo {
  country?: string;
  countryId?: string;
  countryName?: string;
  stateId?: string;
  stateCode?: string;
  stateName?: string;
  lgaId?: string;
  lgaCode?: string;
  lgaName?: string;
}

/**
 * Country → State → City (Local Government) address service backed by the
 * country-data API. This lives alongside the legacy `AddressService`
 * (State → Local Govt) without replacing it, so existing consumers are
 * unaffected.
 *
 * Every level is normalized to `AddressOption` ({ code, name }). The *shared
 * observable* is cached (not just its value) with `shareReplay`, so concurrent
 * callers — e.g. the client and next-of-kin blocks both defaulting to the same
 * country — reuse a single in-flight HTTP request instead of firing duplicates.
 */
@Injectable({
  providedIn: 'root',
})
export class CountryAddressService {
  private readonly baseUrl =
    environment.countryBaseUrl + environment.countryUrl;

  private countries$?: Observable<CountryData[]>;
  private states$ = new Map<string, Observable<StateData[]>>();
  /**
   * uuid -> stateCode, filled in as state lists resolve. The
   * local-governments endpoint keys off the stateCode, so when a form binds
   * the state's uuid as its option value this lets the LGA lookup translate it
   * back without the caller having to care which shape it holds.
   */
  private stateCodeByUuid = new Map<string, string>();
  private stateUuidByCode = new Map<string, string>();
  /**
   * Everything else known about each loaded country/state/LGA, keyed by uuid
   * (country: by code), so withFullGeo() can send the full geography on save.
   */
  private countryByCode = new Map<string, { uuid: string; name: string }>();
  private stateByUuid = new Map<string, { name: string; countryCode: string }>();
  private lgaByUuid = new Map<string, { name: string; code?: string }>();
  private lgas$ = new Map<string, Observable<LocalGovernmentData[]>>();
  private banks$ = new Map<string, Observable<AddressOption[]>>();
  private banksWithCode$ = new Map<string, Observable<AddressOption[]>>();
  private idTypes$ = new Map<string, Observable<IdTypeOption[]>>();

  private tenantCountry$?: Observable<string>;

  constructor(
    private http: HttpClient,
    private data: DataService,
  ) {}

  /**
   * The tenant's country code (e.g. `NGA`, `GHA`), from the organization
   * endpoint. Resolved once and shared — the bank and ID-type lists are the
   * same for every form in the app, so each consumer should not re-derive it.
   * Falls back to `NGA` so a failed lookup degrades to the previous default
   * rather than an empty dropdown.
   */
  private tenantCountryCode(): Observable<string> {
    if (!this.tenantCountry$) {
      this.tenantCountry$ = this.data.getOrganization().pipe(
        map((res: any) => res?.data?.tenantCountry?.countryCode || 'NGA'),
        catchError(() => of('NGA')),
        shareReplay(1),
      );
    }
    return this.tenantCountry$;
  }

  /** The tenant's country code (e.g. `NGA`), to default a Country dropdown. */
  public getTenantCountryCode(): Observable<string> {
    return this.tenantCountryCode();
  }

  /** Banks for the tenant's own country. See getBanks for the value contract. */
  public getTenantBanks(): Observable<AddressOption[]> {
    return this.tenantCountryCode().pipe(
      switchMap((code) => this.getBanks(code)),
    );
  }

  /**
   * Banks for the tenant's own country, keyed by the real bank code. See
   * getBanksWithCode for how this differs from getTenantBanks.
   */
  public getTenantBanksWithCode(): Observable<AddressOption[]> {
    return this.tenantCountryCode().pipe(
      switchMap((code) => this.getBanksWithCode(code)),
    );
  }

  /** ID document types for the tenant's own country. */
  public getTenantIdentityDocumentTypes(): Observable<IdTypeOption[]> {
    return this.tenantCountryCode().pipe(
      switchMap((countryCode) => this.rawIdentityDocumentTypes(countryCode)),
    );
  }

  /** Identity document type records for the tenant's own country (incl. hasExpiry). */
  public getTenantIdentityDocumentTypeData(): Observable<
    IdentityDocumentTypeData[]
  > {
    return this.tenantCountryCode().pipe(
      switchMap((code) => this.getIdentityDocumentTypeData(code)),
    );
  }

  /** States/regions for the tenant's own country (country-data API, { code, name }). */
  public getTenantStates(): Observable<AddressOption[]> {
    return this.tenantCountryCode().pipe(
      switchMap((code) => this.getStates(code)),
    );
  }

  /**
   * Raw country records, cached (shared) after the first call.
   *
   * Most callers want `getCountries()` for `{ code, name }` dropdown options;
   * this exists for the ones that need the richer per-country fields the API
   * also returns — notably `subNationalLabel`/`localLevelLabel`, which drive
   * the address terminology for the country the *form* selected rather than
   * the tenant's own country.
   */
  public getCountryData(): Observable<CountryData[]> {
    if (!this.countries$) {
      this.countries$ = this.http
        .get<unknown>(`${this.baseUrl}countries`)
        .pipe(
          map((res) => this.unwrap<CountryData>(res)),
          tap((countries) =>
            countries.forEach((c) => {
              if (c.countryCode && c.uuid) {
                this.countryByCode.set(c.countryCode.toUpperCase(), {
                  uuid: c.uuid,
                  name: c.countryName,
                });
              }
            }),
          ),
          catchError((err) => {
            this.countries$ = undefined; // allow a retry after a failure
            return throwError(() => err);
          }),
          shareReplay(1),
        );
    }
    return this.countries$;
  }

  /** Countries as `{ code, name }` dropdown options, sorted by name. */
  public getCountries(): Observable<AddressOption[]> {
    return this.getCountryData().pipe(
      map((countries) =>
        this.sortByName(
          countries.map((c) => ({
            code: c.countryCode ?? c.uuid,
            name: c.countryName,
          })),
        ),
      ),
    );
  }

  /**
   * Raw states for a country, cached per country code so both option shapes
   * below share a single request. Also records each uuid -> stateCode pairing
   * for the LGA lookup.
   */
  private rawStates(countryCode: string): Observable<StateData[]> {
    let cached = this.states$.get(countryCode);
    if (!cached) {
      cached = this.http
        .get<unknown>(
          `${this.baseUrl}states/by-country-code?countryName=${countryCode}`,
        )
        .pipe(
          map((res) => this.unwrap<StateData>(res)),
          tap((states) =>
            states.forEach((st) => {
              if (st.uuid && st.stateCode) {
                this.stateCodeByUuid.set(st.uuid, st.stateCode);
                this.stateUuidByCode.set(st.stateCode.toUpperCase(), st.uuid);
              }
              if (st.uuid) {
                this.stateByUuid.set(st.uuid, {
                  name: st.stateName,
                  countryCode,
                });
              }
            }),
          ),
          catchError((err) => {
            this.states$.delete(countryCode);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.states$.set(countryCode, cached);
    }
    return cached;
  }

  /**
   * States for a country (by country code, e.g. `NGA`), sorted by name.
   *
   * The option value is the state's `uuid` — what the backend expects for
   * every form (client, corporate, staff, branch). The `stateCode` is not
   * exposed as an option value at all; getLocalGovernments translates a uuid
   * back to it internally, since that endpoint still keys off the code.
   */
  public getStates(countryCode: string): Observable<AddressOption[]> {
    if (!countryCode) return of([]);
    return this.rawStates(countryCode).pipe(
      map((sts) =>
        this.sortByName(
          sts.map((st) => ({ code: st.uuid, name: st.stateName })),
        ),
      ),
    );
  }

  /**
   * Find the state option for a saved value. Stored addresses hold the state as
   * a uuid, an ISO stateCode (e.g. `NG-LA`) or a name ("Lagos" / "Lagos State"),
   * while options are keyed by uuid. Call after getStates() has emitted.
   */
  public matchState(
    options: AddressOption[],
    saved: string,
  ): AddressOption | undefined {
    if (!saved || !options?.length) return undefined;
    const raw = String(saved).trim();
    const uuid = this.stateUuidByCode.get(raw.toUpperCase());
    const name = this.normalizeGeoName(raw);
    return (
      options.find((o) => o.code === raw) ??
      (uuid ? options.find((o) => o.code === uuid) : undefined) ??
      options.find((o) => this.normalizeGeoName(o.name) === name)
    );
  }

  /** Find the LGA option for a saved value (uuid or name). */
  public matchLga(
    options: AddressOption[],
    saved: string,
  ): AddressOption | undefined {
    if (!saved || !options?.length) return undefined;
    const raw = String(saved).trim();
    const name = this.normalizeGeoName(raw);
    return (
      options.find((o) => o.code === raw) ??
      options.find((o) => this.normalizeGeoName(o.name) === name)
    );
  }

  /**
   * An address payload with the full country-service geography core stores:
   * `country` (code) + `countryId`/`countryName`, `stateId` (uuid) +
   * `stateCode`/`stateName`, `lgaId` (uuid) + `lgaCode`/`lgaName`. Pass the
   * form's value with the dropdown selections in `country`, `stateId` and
   * `lgaId`. Gaps are filled from the lists the dropdowns already loaded;
   * whatever is still unknown core resolves from its synced tables.
   */
  public withFullGeo<T extends Record<string, any>>(address: T): T & SavedGeo {
    if (!address || typeof address !== 'object') return address;
    const text = (v: unknown) => (v == null ? '' : String(v).trim());

    // A state dropdown value is a uuid; an older value may be its ISO code.
    let stateId = text(address['stateId']);
    stateId = this.stateUuidByCode.get(stateId.toUpperCase()) ?? stateId;
    const state = this.stateByUuid.get(stateId);
    const lgaId = text(address['lgaId']);
    const lga = this.lgaByUuid.get(lgaId);
    const countryCode = text(address['country']) || state?.countryCode || '';
    const country = this.countryByCode.get(countryCode.toUpperCase());

    const geo: SavedGeo = {
      country: countryCode || undefined,
      countryId: text(address['countryId']) || country?.uuid,
      countryName: text(address['countryName']) || country?.name,
      stateId: stateId || undefined,
      stateCode: this.stateCodeByUuid.get(stateId),
      stateName: state?.name ?? (text(address['stateName']) || undefined),
      lgaId: lgaId || undefined,
      lgaCode: lga?.code,
      lgaName: lga?.name ?? (text(address['lgaName']) || undefined),
    };
    // Drop unknowns so they neither overwrite the caller's values nor send nulls.
    Object.keys(geo).forEach((k) => {
      if (!geo[k as keyof SavedGeo]) delete geo[k as keyof SavedGeo];
    });
    return { ...address, ...geo };
  }

  /**
   * The saved geography of a fetched address, as dropdown keys: the stored
   * uuids first, then codes/names for rows saved before uuids were stored.
   * Feed stateKey/lgaKey to matchState/matchLga.
   */
  public savedGeoKeys(address: any): {
    country: string;
    stateKey: string;
    lgaKey: string;
  } {
    return {
      country: address?.country ?? '',
      stateKey:
        address?.stateId ??
        address?.state?.uuid ??
        address?.stateCode ??
        address?.state?.code ??
        address?.stateName ??
        address?.state?.name ??
        '',
      lgaKey:
        address?.lgaId ??
        address?.lga?.uuid ??
        address?.lgaName ??
        address?.lga?.name ??
        '',
    };
  }

  private normalizeGeoName(value: string): string {
    return (value ?? '')
      .toLowerCase()
      .replace(/\s+state$/, '')
      .replace(/[^a-z0-9]/g, '');
  }

  /**
   * Raw local governments for a state, cached per state code so both option
   * shapes below share a single request.
   */
  private rawLocalGovernments(
    stateKey: string,
    quiet = false,
  ): Observable<LocalGovernmentData[]> {
    // The caller may hand us either a stateCode or a state uuid, depending on
    // which option shape its form binds. Normalise to the code the endpoint
    // expects, so both share one cache entry.
    const stateCode = this.stateCodeByUuid.get(stateKey) ?? stateKey;
    let cached = this.lgas$.get(stateCode);
    if (!cached) {
      cached = this.http
        .get<unknown>(
          `${this.baseUrl}local-governments?stateCode=${stateCode}`,
          quiet ? { context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true) } : {},
        )
        .pipe(
          map((res) => this.unwrap<LocalGovernmentData>(res)),
          tap((lgs) =>
            lgs.forEach((lg) => {
              if (lg.uuid) {
                this.lgaByUuid.set(lg.uuid, {
                  name: this.lgaName(lg),
                  code:
                    lg.localGovernmentCode ?? lg.lgaCode ?? lg.lgCode ?? lg.code,
                });
              }
            }),
          ),
          catchError((err) => {
            this.lgas$.delete(stateCode);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.lgas$.set(stateCode, cached);
    }
    return cached;
  }

  /**
   * Local governments for a state, sorted by name. Accepts the state's uuid
   * (what every state dropdown now binds) or its stateCode.
   *
   * The option value is the local government's `uuid` — what the backend
   * expects for every form (client, corporate, staff, branch).
   */
  public getLocalGovernments(stateKey: string, quiet = false): Observable<AddressOption[]> {
    if (!stateKey) return of([]);
    // `quiet`: the caller handles a failure itself (e.g. hides the field), so no global error toast.
    return this.rawLocalGovernments(stateKey, quiet).pipe(
      map((lgs) =>
        this.sortByName(
          lgs.map((lg) => ({ code: lg.uuid, name: this.lgaName(lg) })),
        ),
      ),
    );
  }

  /**
   * Banks for a country (by country code, e.g. `GHA`), sorted by name. Cached
   * per country.
   *
   * `code` carries `bankCode` (e.g. `110005`) — the country-service identifier
   * for the bank, and what we send on client creation. `name` is the label only.
   */
  public getBanks(countryCode: string): Observable<AddressOption[]> {
    if (!countryCode) return of([]);
    let cached = this.banks$.get(countryCode);
    if (!cached) {
      cached = this.http
        .get<unknown>(`${this.baseUrl}banks/by-country-code?countryCode=${countryCode}`)
        .pipe(
          map((res) =>
            this.sortByName(
              this.unwrap<BankData>(res)
                .filter((b) => b.isActive !== false)
                .map((b) => ({ code: b.bankCode, name: b.bankName })),
            ),
          ),
          catchError((err) => {
            this.banks$.delete(countryCode);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.banks$.set(countryCode, cached);
    }
    return cached;
  }

  /**
   * Banks for a country, with `code` carrying the real `bankCode`.
   *
   * getBanks above now binds the bankCode too. This differs only in that an
   * entry the endpoint returns without a bankCode falls back to its name, so
   * it still shows in the dropdown. The vendor and loan-disbursement forms use
   * it; it can fold into getBanks once every bank is known to carry a code.
   */
  public getBanksWithCode(countryCode: string): Observable<AddressOption[]> {
    if (!countryCode) return of([]);
    let cached = this.banksWithCode$.get(countryCode);
    if (!cached) {
      cached = this.http
        .get<unknown>(`${this.baseUrl}banks/by-country-code?countryCode=${countryCode}`)
        .pipe(
          map((res) =>
            this.sortByName(
              this.unwrap<BankData>(res)
                .filter((b) => b.isActive !== false)
                .map((b) => ({ code: b.bankCode || b.bankName, name: b.bankName })),
            ),
          ),
          catchError((err) => {
            this.banksWithCode$.delete(countryCode);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.banksWithCode$.set(countryCode, cached);
    }
    return cached;
  }

  /**
   * Identity document types for a country (by country code, e.g. `GHA`), sorted
   * by name. Cached per country.
   *
   * Unlike banks, `code` here is a real slug (`drivers-licence`, `ghana-card`)
   * and is what the backend expects, so it is the option value while `name` is
   * the label.
   */
  /**
   * Raw identity document types for a country, cached per country code so the
   * option list and the richer records share a single request. Keeps the
   * fields the dropdown drops — notably `hasExpiry`.
   */
  private rawIdentityDocumentTypes(
    countryCode: string,
  ): Observable<IdTypeOption[]> {
    if (!countryCode) return of([]);
    let cached: Observable<IdTypeOption[]> | undefined = this.idTypes$.get(countryCode);
    if (!cached) {
      cached = this.http
        .get<unknown>(
          `${this.baseUrl}identity-document-types/by-country-code?countryCode=${countryCode}`,
        )
        .pipe(
          map((res) =>
            this.sortByName(
              this.unwrap<IdentityDocumentTypeData>(res)
                .filter((d) => d.isActive !== false)
                .map((d) => ({
                  uuid: d.uuid,
                  code: d.code,
                  name: d.name,
                  hasExpiry: d.hasExpiry ?? false,
                })),
            ),
          ),
          catchError((err) => {
            this.idTypes$.delete(countryCode);
            return throwError(() => err);
          }),
          shareReplay(1),
        );
      this.idTypes$.set(countryCode, cached);
    }
    return cached;
  }

  public getIdentityDocumentTypes(
    countryCode: string,
  ): Observable<AddressOption[]> {
    if (!countryCode) return of([]);
    return this.rawIdentityDocumentTypes(countryCode).pipe(
      map((types) =>
        this.sortByName(types.map((d) => ({ code: d.code, name: d.name }))),
      ),
    );
  }

  /**
   * The same list as records rather than options, for callers that need the
   * fields `AddressOption` cannot carry — `hasExpiry` drives whether an
   * Expiry Date field applies to the selected document.
   */
  public getIdentityDocumentTypeData(
    countryCode: string,
  ): Observable<IdentityDocumentTypeData[]> {
    if (!countryCode) return of([]);
    return this.rawIdentityDocumentTypes(countryCode);
  }

  /**
   * The country-data API nests the list under a different key per endpoint:
   * countries → `data`, states → `states`, local governments → `localGovernments`.
   * Some endpoints instead return a bare array. Pull the array out wherever it
   * lives: prefer the known wrapper keys, otherwise take the first array-valued
   * property (one level deep for paginated `data.content` shapes).
   */
  private unwrap<T>(res: unknown): T[] {
    if (Array.isArray(res)) return res as T[];
    if (!res || typeof res !== 'object') return [];

    const obj = res as Record<string, unknown>;
    const KNOWN_KEYS = [
      'data',
      'states',
      'localGovernments',
      'countries',
      'content',
    ];
    for (const key of KNOWN_KEYS) {
      if (Array.isArray(obj[key])) return obj[key] as T[];
    }

    // Fallback: first array-valued property, then one level deeper.
    const values = Object.values(obj);
    const topArray = values.find((v) => Array.isArray(v));
    if (topArray) return topArray as T[];
    for (const v of values) {
      if (v && typeof v === 'object') {
        const inner = Object.values(v as Record<string, unknown>).find((x) =>
          Array.isArray(x),
        );
        if (inner) return inner as T[];
      }
    }
    return [];
  }

  /**
   * Normalize a local government to `AddressOption`. The endpoint's field
   * names are resolved defensively so a naming change on the API side does not
   * silently break the dropdown.
   */
  /** The display name, resolved defensively across the endpoint's field spellings. */
  private lgaName(lg: LocalGovernmentData): string {
    return lg.localGovernmentName ?? lg.lgaName ?? lg.lgName ?? lg.name ?? '';
  }

  private sortByName<T extends AddressOption>(options: T[]): T[] {
    return options.sort((a, b) => a.name.localeCompare(b.name));
  }
}
